import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';
import AssignmentFilters from './components/AssignmentFilters';
import AssignmentsList from './components/AssignmentsList';
import BulkActionsBar from './components/BulkActionsBar';
import BulkConfirmModal from './components/BulkConfirmModal';
import KpiDeTareas from './components/KpiDeTareas';
import ExpedientesSinTarea from './components/ExpedientesSinTarea';
import userStore from '../../store/userStore';
import axios from 'axios';
import { baseAPI } from '../../config';
import Pagination from '../../components/Pagination';

/** Params que pueden llegar como lista separada por comas desde los filtros. */
const MULTI_VALUE_PARAMS = ['status', 'priority', 'task_type', 'estado'];

/**
 * Filtro para el endpoint de bulk a partir de la URL. Debe producir lo mismo
 * que el query string del listado: los params multivalor van como array, ya que
 * el backend (buildAssignmentMatchFilter) trataría "pending,in_progress" como
 * un único valor literal.
 */
const buildFilterFromParams = searchParams => {
  const filter = {};
  for (const [key, value] of searchParams.entries()) {
    if (key === 'page') continue;
    filter[key] = MULTI_VALUE_PARAMS.includes(key) ? value.split(',').map(v => v.trim()) : value;
  }
  return filter;
};

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const WITHOUT_TASK_VIEW = 'without-task';

/** Filtros del expediente: valen igual en la lista de tareas y en la de sin tarea. */
const SHARED_PARAMS = ['client_id', 'estado', 'deudor', 'importe_min', 'importe_max'];

const AssignmentsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
  });

  // Selección. Dos modos: ids concretos, o "todos los que cumplen el filtro"
  // (con exclusiones), que es lo que permite operar más allá de la página.
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [excludedIds, setExcludedIds] = useState([]);

  const [preview, setPreview] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(null);
  const [changeDescriptions, setChangeDescriptions] = useState([]);
  const [applying, setApplying] = useState(false);

  const currentPage = parseInt(searchParams.get('page')) || 1;
  const userToken = localStorage.getItem('token');

  // La vista «Expedientes sin tarea» cruza todos los clientes: solo admin,
  // igual que el panel de control (el endpoint también lo exige).
  // El panel y la lista «sin tarea»: el admin los ve de todos; el gestor y la
  // empresa, de lo suyo (el servidor los recorta a su ámbito de Tareas).
  const role = userStore(state => state.user?.role);
  const canSeePanel = ['admin', 'gestor', 'company'].includes(role);
  const showWithoutTask = canSeePanel && searchParams.get('view') === WITHOUT_TASK_VIEW;

  // Al cambiar de vista se conservan los filtros que valen en las dos; los de
  // tarea (fechas, tipo, prioridad…) no tienen sentido en la otra.
  const switchView = view => {
    const next = new URLSearchParams();
    for (const key of SHARED_PARAMS) {
      const value = searchParams.get(key);
      if (value) next.set(key, value);
    }
    if (view === WITHOUT_TASK_VIEW) next.set('view', WITHOUT_TASK_VIEW);
    setSearchParams(next);
  };

  // Los filtros (todo menos la página) definen el conjunto seleccionable: si
  // cambian, la selección deja de tener sentido y se limpia.
  const filterSignature = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    params.sort();
    return params.toString();
  }, [searchParams]);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectAllMatching(false);
    setExcludedIds([]);
  }, []);

  useEffect(() => {
    clearSelection();
  }, [filterSignature, clearSelection]);

  useEffect(() => {
    // Check authentication
    if (userToken == null) {
      window.location = '/login';
      return;
    }
    // La otra vista pide lo suyo.
    if (showWithoutTask) return;
    const fetchAssignments = async () => {
      try {
        setLoading(true);

        // Build query params from URL
        const params = new URLSearchParams();

        // Copy all params from searchParams
        for (const [key, value] of searchParams.entries()) {
          // For multi-value params (comma-separated), add each value separately
          if (MULTI_VALUE_PARAMS.includes(key) && value.includes(',')) {
            value.split(',').forEach(v => params.append(key, v.trim()));
          } else {
            params.append(key, value);
          }
        }

        params.set('page', currentPage);
        params.set('limit', '20');

        const response = await axios.get(`${baseAPI}/assignments?${params.toString()}`, authHeaders());

        setAssignments(response.data?.assignments || []);
        setPagination(response.data?.pagination || {});
      } catch (error) {
        console.error('Error fetching assignments:', error);
        if (error.response?.status === 403) {
          window.location = '/dashboard';
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [searchParams, currentPage, userToken, refreshKey, showWithoutTask]);

  const handlePageChange = page => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page);
    setSearchParams(newParams);
  };

  const isSelected = useCallback(
    id => (selectAllMatching ? !excludedIds.includes(id) : selectedIds.includes(id)),
    [selectAllMatching, excludedIds, selectedIds]
  );

  const handleToggleOne = id => {
    if (selectAllMatching) {
      setExcludedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
      return;
    }
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  // El check de cabecera selecciona TODOS los que cumplen el filtro, no solo
  // los de la página visible.
  const handleToggleAll = () => {
    if (selectAllMatching || selectedIds.length > 0) {
      clearSelection();
      return;
    }
    setSelectAllMatching(true);
    setExcludedIds([]);
  };

  const totalItems = pagination.total_items || 0;
  const selectedCount = selectAllMatching ? Math.max(totalItems - excludedIds.length, 0) : selectedIds.length;

  const maxBulkSize = preview?.max_bulk_size || 500;
  const cappedSelection = selectAllMatching && selectedCount > maxBulkSize;

  const headerChecked = selectAllMatching && excludedIds.length === 0;
  const headerIndeterminate =
    (selectAllMatching && excludedIds.length > 0) || (!selectAllMatching && selectedIds.length > 0);

  const buildSelection = () =>
    selectAllMatching
      ? { mode: 'filter', filter: buildFilterFromParams(searchParams), exclude_ids: excludedIds }
      : { mode: 'ids', ids: selectedIds };

  const errorMessage = (error, fallback) => {
    if (error.response?.status === 401) return 'Sesión expirada. Por favor, inicia sesión nuevamente';
    return error.response?.data?.error || fallback;
  };

  // Paso 1: preview. Nunca se aplica nada sin enseñar antes el recuento real.
  const handleApply = async (changes, descriptions) => {
    if (!changes) return;
    try {
      setApplying(true);
      const { data } = await axios.post(
        `${baseAPI}/assignments/bulk/preview`,
        { selection: buildSelection(), changes },
        authHeaders()
      );

      if (!data.affected) {
        toast.info('Ningún assignment coincide con la selección');
        return;
      }

      setPendingChanges(changes);
      setChangeDescriptions(descriptions);
      setPreview(data);
    } catch (error) {
      console.error('Error previewing bulk changes:', error);
      toast.error(errorMessage(error, 'Error al calcular los assignments afectados'));
    } finally {
      setApplying(false);
    }
  };

  // Paso 2: aplicar. La selección se envía otra vez igual, así que el conjunto
  // se recalcula en el servidor; por eso se informa del `modified` real.
  const handleConfirm = async () => {
    try {
      setApplying(true);
      const { data } = await axios.patch(
        `${baseAPI}/assignments/bulk`,
        { selection: buildSelection(), changes: pendingChanges },
        authHeaders()
      );

      toast.success(
        `${data.modified} assignment${data.modified === 1 ? '' : 's'} actualizado${data.modified === 1 ? '' : 's'}`
      );
      handleCancelConfirm();
      clearSelection();
      setRefreshKey(k => k + 1);
    } catch (error) {
      console.error('Error applying bulk changes:', error);
      toast.error(errorMessage(error, 'Error al aplicar los cambios'));
    } finally {
      setApplying(false);
    }
  };

  const handleCancelConfirm = () => {
    setPreview(null);
    setPendingChanges(null);
    setChangeDescriptions([]);
  };

  return (
    <MainLayout>
      <div className='col-span-full xl:col-span-12 bg-white shadow-lg rounded-lg border border-slate-200'>
        <header className='px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3'>
          <h1 className='text-2xl font-semibold text-slate-900'>
            {showWithoutTask ? (
              'Expedientes sin tarea'
            ) : (
              <>
                Gestiones <small className='text-slate-500'>({pagination.total_items})</small>
              </>
            )}
          </h1>
          {canSeePanel && (
            <div className='inline-flex rounded-lg border border-slate-200 p-0.5 text-sm' role='tablist'>
              {[
                { view: null, label: 'Tareas' },
                { view: WITHOUT_TASK_VIEW, label: 'Expedientes sin tarea' },
              ].map(tab => {
                const active = (tab.view === WITHOUT_TASK_VIEW) === showWithoutTask;
                return (
                  <button
                    key={tab.label}
                    type='button'
                    role='tab'
                    aria-selected={active}
                    onClick={() => !active && switchView(tab.view)}
                    className={`px-3 py-1 rounded-lg ${
                      active ? 'bg-indigo-500 text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
        </header>

        <div className='p-5'>
          {/* Qué está colgado: lo que NO sale en la lista de abajo */}
          {canSeePanel && <KpiDeTareas />}

          {/* Filters */}
          <AssignmentFilters mode={showWithoutTask ? 'expedients' : 'tasks'} />

          {showWithoutTask ? (
            <div className='mt-6'>
              <ExpedientesSinTarea />
            </div>
          ) : (
            <>
              {/* Bulk actions */}
              {role === 'admin' && selectedCount > 0 && (
                <BulkActionsBar
                  selectedCount={selectedCount}
                  capped={cappedSelection}
                  maxBulkSize={maxBulkSize}
                  onClear={clearSelection}
                  onApply={handleApply}
                  applying={applying && !preview}
                />
              )}

              {/* Assignments List */}
              <div className='mt-6'>
                {loading ? (
                  <div className='text-center py-8'>
                    <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500'></div>
                    <p className='text-slate-500 mt-2'>Cargando...</p>
                  </div>
                ) : assignments.length === 0 ? (
                  <div className='text-center py-8'>
                    <p className='text-slate-600 font-medium'>Todo al día</p>
                    <p className='text-sm text-slate-500 mt-1'>No hay tareas con estos filtros.</p>
                  </div>
                ) : (
                  <AssignmentsList
                    assignments={assignments}
                    isSelected={isSelected}
                    onToggleOne={handleToggleOne}
                    headerChecked={headerChecked}
                    headerIndeterminate={headerIndeterminate}
                    onToggleAll={handleToggleAll}
                    selectable={role === 'admin'}
                    showClient={role === 'admin'}
                  />
                )}
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className='mt-6 flex justify-center'>
                  <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <BulkConfirmModal
        show={Boolean(preview)}
        preview={preview}
        changeDescriptions={changeDescriptions}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
        applying={applying}
      />
    </MainLayout>
  );
};

export default AssignmentsPage;
